import {createEntityMeta,SyncStatus} from '../offline/entity.js';
import {createProbe,deactivateProbe,updateProbe} from './thermometer.js';

export const PROBE_ENTITY_TYPE='thermometer_probe';

function requireDependencies(repository,queue,thermometerStore){
  if(!repository?.get||!repository?.list||!repository?.put)throw new TypeError('repository with get/list/put is required');
  if(!queue?.enqueue||!queue?.markConflict)throw new TypeError('sync queue contract is required');
  if(!thermometerStore?.get)throw new TypeError('thermometerStore.get is required');
}

function withMeta(existing,domain,userId){
  const value={...(existing||{})};
  delete value.key;
  delete value.entityType;
  return {
    ...(existing?value:createEntityMeta({id:domain.id,syncStatus:SyncStatus.PENDING_CREATE})),
    ...domain,
    id:domain.id,
    userId
  };
}

export function createProbeStore({
  repository,
  queue,
  thermometerStore,
  now=()=>new Date().toISOString()
}={}){
  requireDependencies(repository,queue,thermometerStore);

  async function get(id){
    return repository.get(PROBE_ENTITY_TYPE,String(id||'').trim());
  }

  async function list({thermometerId=null,userId=null,includeInactive=false}={}){
    const rows=await repository.list(PROBE_ENTITY_TYPE);
    return Object.freeze((rows||[])
      .filter(row=>!thermometerId||row.thermometerId===thermometerId)
      .filter(row=>!userId||row.userId===userId)
      .filter(row=>includeInactive||!row.inactiveAt)
      .sort((a,b)=>
        Number(a.channelIndex??999)-Number(b.channelIndex??999)
        ||String(a.label||'').localeCompare(String(b.label||''))
        ||String(a.id||'').localeCompare(String(b.id||''))
      ));
  }

  async function create(input={}){
    const thermometer=await thermometerStore.get(input.thermometerId);
    if(!thermometer||thermometer.archivedAt)throw new TypeError('Active thermometer is required');
    const domain=createProbe(input);
    const existing=await list({thermometerId:domain.thermometerId});
    if(domain.channelIndex!=null&&existing.some(row=>row.channelIndex===domain.channelIndex)){
      throw new TypeError('Active probe channel is already configured');
    }
    const entity={
      ...createEntityMeta({id:domain.id,now:now(),syncStatus:SyncStatus.PENDING_CREATE}),
      ...domain,
      userId:thermometer.userId
    };
    return queue.enqueue(PROBE_ENTITY_TYPE,entity,'create');
  }

  async function edit(id,changes={}){
    const current=await get(id);
    if(!current)throw new TypeError('Probe not found');
    if(current.syncStatus===SyncStatus.CONFLICT)throw new Error('PROBE_CONFLICT');
    const domain=updateProbe(current,changes);
    if(domain.channelIndex!=null){
      const existing=await list({thermometerId:domain.thermometerId});
      if(existing.some(row=>row.id!==domain.id&&row.channelIndex===domain.channelIndex)){
        throw new TypeError('Active probe channel is already configured');
      }
    }
    return queue.enqueue(PROBE_ENTITY_TYPE,withMeta(current,domain,current.userId),'update');
  }

  async function deactivate(id){
    const current=await get(id);
    if(!current)throw new TypeError('Probe not found');
    if(current.syncStatus===SyncStatus.CONFLICT)throw new Error('PROBE_CONFLICT');
    const domain=deactivateProbe(current,{now:now()});
    return queue.enqueue(PROBE_ENTITY_TYPE,withMeta(current,domain,current.userId),'update');
  }

  async function markConflict(id,conflict){
    const current=await get(id);
    if(!current)throw new TypeError('Probe not found');
    return queue.markConflict(PROBE_ENTITY_TYPE,current,conflict);
  }

  return Object.freeze({get,list,create,edit,deactivate,markConflict});
}
